/* eslint-disable */
import { useCompanyService } from '@packages/core/company/company-context.ts';
import { useCompanies } from '@packages/core/company/hook/use-companies.ts';
import { useCompanyCount } from '@packages/core/company/hook/use-company-count.ts';
import { useNavigate } from 'react-router-dom';

import Card from 'ui/src/card/card';
import CardHeader from 'ui/src/card/card-header';
import PrimaryButton from 'ui/src/button/primary-button';
import Table from '@packages/ui/table/table.tsx';
import TableRowColumn from '@packages/ui/table/table-row-cell.tsx';
import TableRow from '@packages/ui/table/table-row.tsx';
import TableHeaderColumn from '@packages/ui/table/table-header-column.tsx';
import Loading from '@packages/ui/loading.tsx';
import CardTableContent from '@packages/ui/card/card-content-table.tsx';
import { useAuth } from '@client/module/auth/auth-context.ts';
import HistorySearchBar from '@client/page/management/appointment/history/component/history-search-bar.tsx';
import { useCallback, useEffect, useState } from 'react';
import { CompanyCategory } from '@client/graphql/types/graphql.ts';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import { useTranslate } from '@tolgee/react';
import { TableCellAlignment } from '@packages/ui/table/table-type.ts';
import usePagination from '@packages/core/utils/pagination/use-pagination';
import { ButtonGroup, IconButton, Pagination } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface CompanyData {
  __typename?: 'Company' | undefined;
  id: string;
  name: string;
  category: CompanyCategory[];
  settings: {
    customerDefaultLanguage: string;
  };
}

const CompanySelectionPage = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { setCompanyID } = useCompanyService();
  const { t } = useTranslate();

  // Pagination setup
  const { data: countData, loading: countLoading, error: countError } = useCompanyCount();
  const paginationInfo = usePagination(countData?.companyCount ?? 0, { limit: 300 });
  const { data, loading, error } = useCompanies(
    paginationInfo.info.current * paginationInfo.info.perPage,
    paginationInfo.info.perPage,
  );

  const [companies, setCompanies] = useState([] as CompanyData[]);

  useEffect(() => {
    if (data) {
      setCompanies(data.companies);
    }
  }, [data]);

  const onCompanyClick = (id: string) => {
    const companyData = data?.companies.find((company) => company.id === id);
    if (companyData) {
      localStorage.setItem('language', companyData.settings.customerDefaultLanguage);
    }
    setCompanyID(id);
    navigate('/', { replace: true });
  };

  const handleSearchBar = useCallback(
    (searchString: string) => {
      // Reset to first page when searching
      if (data) {
        const regex = new RegExp(searchString, 'i');
        setCompanies(data.companies.filter((company) => regex.test(company.name)));
      }
    },
    [data, paginationInfo],
  );

  if (loading ?? countLoading) {
    return <Loading />;
  }

  if (error ?? countError) {
    return <>Something went wrong</>;
  }

  if (!data) {
    return <div>No companies found</div>;
  }

  const contractButtonAccept = ['ADMIN', 'SUPER_ADMIN', 'CONTRACT_MANAGER'].some((role) => auth?.role.includes(role));

  return (
    <div className="container max-w-4xl  mx-auto px-4 py-8">
      <Card>
        <CardHeader title="Company">
          {auth?.role.includes('ADMIN') && (
            <PrimaryButton
              onClick={() => {
                navigate('create');
              }}
            >
              Create
            </PrimaryButton>
          )}
        </CardHeader>
        <CardTableContent>
          <HistorySearchBar onChange={handleSearchBar} />

          {/* Show message when no search results */}
          {companies.length === 0 && data && data.companies.length > 0 ? (
            <div className="text-center py-4 text-gray-500 text-sm">
              {t('company-selection.no-results', 'No results found')}
            </div>
          ) : (
            <Table
              data={companies}
              build={(item) => {
                return (
                  <TableRow key={item.id}>
                    <TableRowColumn>
                      <div
                        onClick={() => {
                          onCompanyClick(item.id);
                        }}
                      >
                        {item.name}
                      </div>
                    </TableRowColumn>
                    {contractButtonAccept && (
                      <TableRowColumn alignment={TableCellAlignment.Right}>
                        <SecondaryButton
                          onClick={() => {
                            navigate(`/contract-management/${item.id}/contracts`);
                          }}
                        >
                          {t('company-selection.contact-button', 'Contracts')}
                        </SecondaryButton>
                      </TableRowColumn>
                    )}
                  </TableRow>
                );
              }}
              loading={loading}
              error={error}
            >
              <TableHeaderColumn>Name</TableHeaderColumn>
              <TableHeaderColumn></TableHeaderColumn>
            </Table>
          )}

          {/* Pagination */}
          <Pagination.Root
            count={paginationInfo.info.size}
            pageSize={paginationInfo.info.perPage}
            page={paginationInfo.info.current + 1}
            onPageChange={(e) => {
              paginationInfo.updateCurrent(e.page - 1);
            }}
            px={2}
            py={4}
            display={'flex'}
            justifyContent={'end'}
          >
            <ButtonGroup variant="ghost" size="sm">
              <Pagination.PrevTrigger asChild>
                <IconButton>
                  <ChevronLeftIcon />
                </IconButton>
              </Pagination.PrevTrigger>

              <Pagination.Items
                render={(page) => (
                  <IconButton
                    p={2.5}
                    border={'1px solid'}
                    color={paginationInfo.info.current == page.value - 1 ? 'white' : 'gray.500'}
                    fontWeight={'semibold'}
                    borderColor={'gray.300'}
                    bgColor={paginationInfo.info.current == page.value - 1 ? 'gray.500' : 'white'}
                    variant={{ base: 'ghost', _selected: 'outline' }}
                  >
                    {page.value}
                  </IconButton>
                )}
              />

              <Pagination.NextTrigger asChild>
                <IconButton>
                  <ChevronRightIcon />
                </IconButton>
              </Pagination.NextTrigger>
            </ButtonGroup>
          </Pagination.Root>
        </CardTableContent>
      </Card>
    </div>
  );
};

export default CompanySelectionPage;
